import { useMemo } from 'react';

import { useGetApiV1TutoraiUsage } from '@/shared/api/generated';

import { normalizeStudentUsage } from '../utils/normalizeTutorAi';
import { isUsagePeriodValid } from '../utils/usagePeriod';
import { buildDailyTokenSeries } from '../utils/usageTimeline';
import { useTutorUsageFilters } from './useTutorUsageFilters';

/**
 * Relatório agregado de consumo de tokens do tutor no período filtrado.
 *
 * Mesmo endpoint do relatório de um aluno, chamado **sem `personId`** — é assim que a API
 * expressa o recorte agregado. Por consequência a resposta é o mesmo
 * `TutorAITokenUsageReportViewModel`, e a normalização é a mesma
 * (`normalizeStudentUsage`); o nome do tipo (`TutorStudentUsage`) fica devendo, mas
 * duplicá-lo só para renomear campos idênticos custaria mais do que esclarece.
 *
 * `classStudentId` **não** é enviado: o endpoint não aceita esse recorte, e é por isso que
 * a aba esconde o filtro de turma em vez de mandá-lo e ser ignorada.
 *
 * Os campos de limite (`dailyLimit`, `limitEnabled`, `*LimitReached`) vêm no payload mas
 * são configuração por aluno — sem `personId` não têm significado agregado, e a UI desta
 * aba não os exibe.
 *
 * A query só dispara com um período válido (`isUsagePeriodValid`), o mesmo gate do
 * `useTutorDashboardReport`, para as duas abas recusarem os mesmos períodos.
 *
 * @returns Relatório normalizado, `dailySeries` densa para o gráfico (um ponto por dia,
 * zero onde a API omitiu), estados de carregamento/erro, `isPeriodValid` e `refetch`
 *
 * @example
 * ```tsx
 * const { usage, dailySeries, isLoading } = useTutorAggregateUsage();
 * ```
 */
export function useTutorAggregateUsage() {
  const { filters } = useTutorUsageFilters();

  const isPeriodValid = isUsagePeriodValid(filters.dateStart, filters.dateEnd);

  const query = useGetApiV1TutoraiUsage(
    { start: filters.dateStart, end: filters.dateEnd },
    { query: { enabled: isPeriodValid } },
  );

  // Relatório e série saem do mesmo memo: a série é função do relatório e do período, e
  // separá-los recalcularia a densificação a cada render em que só o outro mudasse.
  const { usage, dailySeries } = useMemo(() => {
    const normalized = normalizeStudentUsage(query.data ?? {});
    return {
      usage: normalized,
      dailySeries: buildDailyTokenSeries(normalized.dailyUsage, filters.dateStart, filters.dateEnd),
    };
  }, [query.data, filters.dateStart, filters.dateEnd]);

  return {
    usage,
    dailySeries,
    isLoading: query.isLoading,
    isError: query.isError,
    isPeriodValid,
    refetch: query.refetch,
  } as const;
}
